import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  verifyAll, getVerifyAll, verifyCompetitorAll, getVerifyCompetitorAll,
} from "@/lib/phase3_api";
import { CompetitorPicker } from "@/components/CompetitorVerify";
import { toast } from "sonner";
import { ShieldCheck, Spinner, Crosshair } from "@phosphor-icons/react";

const STAGE_COPY = {
  crawling: "Fetching the live site",
  walkthrough: "Generating missing walkthrough",
  verifying: "Ruling on acceptance criteria",
  done: "Finishing up",
};

function Bar({ passed, failed, unclear, total }) {
  const t = total || 1;
  const pct = (n) => `${Math.round((n / t) * 100)}%`;
  return (
    <div className="h-2 w-full flex overflow-hidden bg-zinc-100">
      <div style={{ width: pct(passed) }} className="bg-emerald-500" />
      <div style={{ width: pct(failed) }} className="bg-red-500" />
      <div style={{ width: pct(unclear) }} className="bg-amber-400" />
    </div>
  );
}

function Progress({ progress, label }) {
  const done = progress?.done || 0;
  const total = progress?.total || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="border border-emerald-300 bg-emerald-50 p-3" data-testid="verify-all-progress">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-800">
        <Spinner size={12} className="animate-spin" /> {label}
        {total ? ` · ${done}/${total} items` : ""}
      </div>
      <div className="mt-2 h-1.5 w-full bg-emerald-100">
        <div className="h-full bg-emerald-600 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1.5 text-sm text-emerald-900">
        {STAGE_COPY[progress?.stage] || "Working"}
        {progress?.current ? ` — ${progress.current}` : ""}. This keeps running if you close the tab.
      </div>
    </div>
  );
}

function Scorecard({ sc, testid }) {
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] uppercase tracking-widest">
        <span className="border border-emerald-300 bg-emerald-100 text-emerald-900 px-2 py-0.5" data-testid={`${testid}-passed`}>
          {sc.passed} pass
        </span>
        <span className="border border-red-300 bg-red-100 text-red-900 px-2 py-0.5" data-testid={`${testid}-failed`}>
          {sc.failed} fail
        </span>
        <span className="border border-amber-300 bg-amber-100 text-amber-900 px-2 py-0.5" data-testid={`${testid}-unclear`}>
          {sc.unclear} unclear
        </span>
        <span className="text-zinc-400 normal-case tracking-normal">
          {sc.total} criteria · {sc.items_checked} items · {sc.subject_url}
        </span>
      </div>
      {sc.summary && <p className="mt-2 text-sm text-zinc-800 leading-relaxed">{sc.summary}</p>}
      <ul className="mt-3 space-y-1.5">
        {(sc.rows || []).map((r, i) => (
          <li key={r.build_item_id || i} className="border border-zinc-200 p-2.5" data-testid={`${testid}-row-${i}`}>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-zinc-400">#{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm text-zinc-900 flex-1 min-w-[160px]">{r.title}</span>
              {r.total > 0 ? (
                <span className="font-mono text-[10px] text-zinc-600">
                  {r.passed}/{r.total} met · {r.failed} fail · {r.unclear} unclear
                </span>
              ) : (
                <span className="font-mono text-[10px] text-amber-700">{r.note || "Not checked"}</span>
              )}
            </div>
            {r.total > 0 && (
              <div className="mt-1.5">
                <Bar passed={r.passed} failed={r.failed} unclear={r.unclear} total={r.total} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function VerifyAllPanel({ analysisId, itemCount, competitors = [] }) {
  const qc = useQueryClient();
  const [queueing, setQueueing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [compUrl, setCompUrl] = useState("");
  const [compQueueing, setCompQueueing] = useState(false);
  const [compPolling, setCompPolling] = useState(false);

  const { data: all } = useQuery({
    queryKey: ["verify-all", analysisId],
    queryFn: () => getVerifyAll(analysisId),
    refetchInterval: polling ? 5000 : false,
  });
  const { data: comp } = useQuery({
    queryKey: ["verify-competitor-all", analysisId],
    queryFn: () => getVerifyCompetitorAll(analysisId),
    refetchInterval: compPolling ? 5000 : false,
  });

  const status = all?.status || "idle";
  const inFlight = status === "queued" || status === "running";
  const sc = all?.scorecard || null;

  const compStatus = comp?.status || "idle";
  const compInFlight = compStatus === "queued" || compStatus === "running";
  const compCards = comp?.scorecards || [];
  const [activeComp, setActiveComp] = useState("");
  const activeCard = compCards.find((c) => c.subject_url === activeComp) || compCards[0] || null;

  useEffect(() => {
    if (inFlight) setPolling(true);
    else if (polling) {
      setPolling(false);
      if (status === "complete") {
        toast.success("Scorecard ready");
        qc.invalidateQueries({ queryKey: ["analysis", analysisId] });
      }
      if (status === "failed") toast.error(all?.error || "Verification failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (compInFlight) setCompPolling(true);
    else if (compPolling) {
      setCompPolling(false);
      if (compStatus === "complete") {
        toast.success("Competitor scorecard ready");
        qc.invalidateQueries({ queryKey: ["analysis", analysisId] });
      }
      if (compStatus === "failed") toast.error(comp?.error || "Competitor verification failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compStatus]);

  const runAll = async () => {
    try {
      setQueueing(true);
      await verifyAll(analysisId);
      setPolling(true);
      toast.info(`Verifying ${itemCount} build items against the live site…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start verification");
    } finally {
      setQueueing(false);
    }
  };

  const runComp = async () => {
    if (!compUrl.trim()) {
      toast.error("Pick a competitor or paste a URL first");
      return;
    }
    const name = (competitors.find((c) => c.homepage_url === compUrl) || {}).name || "";
    try {
      setCompQueueing(true);
      await verifyCompetitorAll(analysisId, compUrl.trim(), name);
      setCompPolling(true);
      toast.info("Running every item's criteria against the competitor…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start competitor verification");
    } finally {
      setCompQueueing(false);
    }
  };

  return (
    <div className="mb-6 border border-zinc-950 bg-white" data-testid="verify-all-panel">
      <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex flex-wrap items-center gap-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 flex-1">
          <ShieldCheck size={12} /> Verification scorecard
        </div>
        <button
          onClick={runAll}
          disabled={queueing || inFlight}
          data-testid="verify-all-button"
          className="bg-zinc-950 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-800 transition-colors disabled:opacity-60 flex items-center gap-1.5"
        >
          {inFlight || queueing ? <Spinner size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
          {inFlight ? "Verifying…" : sc ? "Re-verify all items" : `Verify all ${itemCount} build items`}
        </button>
      </div>

      <div className="p-5">
        {inFlight && <Progress progress={all?.progress} label="Verifying the whole build spec" />}
        {status === "failed" && all?.error && (
          <div className="border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="verify-all-error">
            {all.error}
          </div>
        )}
        {!sc && !inFlight && (
          <div className="text-sm text-zinc-600 leading-relaxed" data-testid="verify-all-empty">
            Check every acceptance criterion in the build spec against the live site in one pass. Missing
            build walkthroughs are generated first so nothing is skipped — expect a few minutes for a full
            spec. Public surfaces only; anything behind a login is reported as unclear.
          </div>
        )}
        {sc && <Scorecard sc={sc} testid="verify-all-scorecard" />}

        {/* Competitor scorecard */}
        <div className="mt-6 border-t border-zinc-200 pt-4" data-testid="competitor-verify-all">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
            <Crosshair size={12} /> Compare against a competitor
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[260px]">
              <CompetitorPicker
                competitors={competitors}
                value={compUrl}
                onChange={setCompUrl}
                testid="competitor-all-picker"
              />
            </div>
            <button
              onClick={runComp}
              disabled={compQueueing || compInFlight}
              data-testid="competitor-verify-all-button"
              className="border border-zinc-950 bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-60 flex items-center gap-1.5"
            >
              {compInFlight || compQueueing ? <Spinner size={11} className="animate-spin" /> : <Crosshair size={11} />}
              {compInFlight ? "Comparing…" : "Compare all items"}
            </button>
          </div>

          {compInFlight && (
            <div className="mt-3">
              <Progress progress={comp?.progress} label="Checking the competitor's live site" />
            </div>
          )}
          {compStatus === "failed" && comp?.error && (
            <div className="mt-3 border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="competitor-verify-all-error">
              {comp.error}
            </div>
          )}

          {compCards.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {compCards.map((c) => (
                <button
                  key={c.subject_url}
                  onClick={() => setActiveComp(c.subject_url)}
                  className={`border px-2 py-0.5 font-mono text-[10px] ${
                    activeCard && activeCard.subject_url === c.subject_url
                      ? "border-zinc-950 bg-zinc-950 text-white"
                      : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100"
                  }`}
                >
                  {c.competitor_name || c.subject_url}
                </button>
              ))}
            </div>
          )}

          {activeCard && sc && (
            <div className="mt-3 border border-zinc-200" data-testid="competitor-gap-table">
              <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Gap by build item — target vs {activeCard.competitor_name || activeCard.subject_url}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left p-2 border-b border-zinc-200">Build item</th>
                    <th className="text-left p-2 border-b border-zinc-200 w-28">Target met</th>
                    <th className="text-left p-2 border-b border-zinc-200 w-28">Rival met</th>
                    <th className="text-left p-2 border-b border-zinc-200 w-36">Read</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeCard.rows || []).map((r, i) => {
                    const mineRow = (sc.rows || []).find((x) => x.build_item_id === r.build_item_id);
                    const minePass = mineRow?.passed || 0;
                    const read =
                      r.passed > minePass
                        ? { t: "Competitor leads", cls: "bg-red-100 text-red-900 border-red-300" }
                        : minePass > r.passed
                        ? { t: "Target leads", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" }
                        : { t: "Neither proves it", cls: "bg-indigo-100 text-indigo-900 border-indigo-300" };
                    return (
                      <tr key={r.build_item_id || i} className="border-b border-zinc-100" data-testid={`competitor-gap-row-${i}`}>
                        <td className="p-2 text-zinc-900">{r.title}</td>
                        <td className="p-2 font-mono text-[11px] text-zinc-700">
                          {mineRow ? `${minePass}/${mineRow.total}` : "—"}
                        </td>
                        <td className="p-2 font-mono text-[11px] text-zinc-700">
                          {r.total > 0 ? `${r.passed}/${r.total}` : "—"}
                        </td>
                        <td className="p-2">
                          <span className={`border px-1.5 py-0.5 font-mono text-[9px] tracking-widest ${read.cls}`}>
                            {read.t}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeCard && !sc && (
            <div className="mt-3 font-mono text-[10px] text-amber-700">
              Verify the target too — then the gap table lines both sides up item by item.
            </div>
          )}

          {!activeCard && !compInFlight && (
            <div className="mt-2 font-mono text-[10px] text-zinc-500">
              Same criteria, their live site. Shows who already proves what, and where the opening is.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
